import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    ArrowLeft, ShieldCheck, ClipboardCheck, FileCheck, AlertTriangle, Plus, Trash2, Save,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

/* Admin Compliance Operations — Regulatory Classification · Launch
 * Readiness · Evidence Register · Incident Register. This is the
 * intent + evidence record, not an auto-compliance claim.
 */
export default function ComplianceOps() {
    return (
        <div className="max-w-6xl mx-auto p-6 md:p-8" data-testid="compliance-ops">
            <div className="mb-4 flex items-center gap-2 text-xs">
                <Link to="/dashboard" className="text-stone-500 hover:text-stone-900 flex items-center gap-1">
                    <ArrowLeft size={12} /> Dashboard
                </Link>
                <span className="text-stone-300">/</span>
                <span className="font-medium">Compliance Operations</span>
            </div>
            <div className="flex items-start gap-3 mb-6">
                <div className="mt-1 rounded-full bg-amber-50 dark:bg-amber-900/30 p-1.5">
                    <ClipboardCheck size={16} className="text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Launch Readiness Assessment</h1>
                    <p className="text-sm text-stone-500 mt-1 max-w-2xl">
                        Track VAULT&apos;s Product Regulatory Classification, Launch Readiness, Compliance Evidence
                        and Security Incidents. Nothing here auto-declares compliance — every status is an admin
                        record, not a public claim.
                    </p>
                </div>
            </div>

            <Tabs defaultValue="readiness">
                <TabsList>
                    <TabsTrigger value="readiness" data-testid="tab-readiness">Launch Readiness</TabsTrigger>
                    <TabsTrigger value="classification" data-testid="tab-classification">Regulatory Classification</TabsTrigger>
                    <TabsTrigger value="evidence" data-testid="tab-evidence">Evidence Register</TabsTrigger>
                    <TabsTrigger value="incidents" data-testid="tab-incidents">Incident Register</TabsTrigger>
                </TabsList>
                <TabsContent value="readiness"><Readiness /></TabsContent>
                <TabsContent value="classification"><Classification /></TabsContent>
                <TabsContent value="evidence"><Evidence /></TabsContent>
                <TabsContent value="incidents"><Incidents /></TabsContent>
            </Tabs>
        </div>
    );
}

function Readiness() {
    const [data, setData] = useState({ categories: [], allowed_statuses: [], blockers: 0, overall_ready: false });
    const load = () => api.get("/admin/compliance/readiness").then((r) => setData(r.data));
    useEffect(() => { load(); }, []);
    const patch = async (category, status) => {
        await api.patch("/admin/compliance/readiness", { category, status });
        toast.success(`${category} → ${status}`);
        load();
    };
    return (
        <div className="mt-4">
            <div className="mb-3 flex items-center gap-3">
                <div className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${data.blockers > 0 ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`} data-testid="readiness-blockers">
                    {data.blockers > 0 ? <AlertTriangle size={12} /> : <ShieldCheck size={12} />}
                    {data.blockers} blocker{data.blockers === 1 ? "" : "s"}
                </div>
                <span className="text-xs text-stone-500">{data.overall_ready ? "All categories ready" : "Assessment in progress — do not launch while any blocker remains"}</span>
            </div>
            <div className="rounded-xl border border-stone-200 dark:border-stone-700 divide-y divide-stone-100 dark:divide-stone-800">
                {data.categories.map((c) => (
                    <div key={c.category} className="flex items-center justify-between gap-4 p-3">
                        <div className="font-medium text-sm">{c.category}</div>
                        <select
                            value={c.status}
                            onChange={(e) => patch(c.category, e.target.value)}
                            data-testid={`readiness-${c.category.replace(/\s+/g, "-").toLowerCase()}`}
                            className="rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1 text-xs"
                        >
                            {data.allowed_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Classification() {
    const [rows, setRows] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const load = () => api.get("/admin/compliance/classification").then((r) => { setRows(r.data.activities); setStatuses(r.data.allowed_statuses); });
    useEffect(() => { load(); }, []);
    const patch = async (activity, patch) => {
        await api.patch("/admin/compliance/classification", { activity, ...patch });
        load();
    };
    return (
        <div className="mt-4 rounded-xl border border-stone-200 dark:border-stone-700 divide-y divide-stone-100 dark:divide-stone-800">
            {rows.map((r) => (
                <div key={r.activity} className="grid grid-cols-1 md:grid-cols-[2fr_auto_auto] items-center gap-3 p-3">
                    <div>
                        <div className="text-sm font-medium capitalize">{r.activity.replace(/_/g, " ")}</div>
                        {r.notes && <div className="text-xs text-stone-500 mt-0.5">{r.notes}</div>}
                    </div>
                    <label className="inline-flex items-center gap-1.5 text-xs">
                        <input
                            type="checkbox"
                            checked={!!r.enabled}
                            onChange={(e) => patch(r.activity, { enabled: e.target.checked, status: r.status, notes: r.notes })}
                            data-testid={`cls-${r.activity}-enabled`}
                        /> Enabled
                    </label>
                    <select
                        value={r.status || "Not Applicable"}
                        onChange={(e) => patch(r.activity, { enabled: !!r.enabled, status: e.target.value, notes: r.notes })}
                        data-testid={`cls-${r.activity}-status`}
                        className="rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1 text-xs"
                    >
                        {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
            ))}
        </div>
    );
}

function Evidence() {
    const [rows, setRows] = useState([]);
    const [f, setF] = useState({ control: "", evidence_type: "policy_document", description: "", url: "", owner: "" });
    const load = () => api.get("/admin/compliance/evidence").then((r) => setRows(r.data.evidence));
    useEffect(() => { load(); }, []);
    const add = async () => {
        if (!f.control) return toast.error("Control required");
        await api.post("/admin/compliance/evidence", f);
        toast.success("Evidence added");
        setF({ control: "", evidence_type: "policy_document", description: "", url: "", owner: "" });
        load();
    };
    const del = async (id) => { await api.delete(`/admin/compliance/evidence/${id}`); load(); };
    return (
        <div className="mt-4 space-y-4">
            <div className="rounded-xl border border-stone-200 dark:border-stone-700 p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3">Record new evidence</div>
                <div className="grid md:grid-cols-2 gap-3">
                    <Input placeholder="Control (e.g. Access review Q1)" value={f.control} onChange={(e) => setF({ ...f, control: e.target.value })} data-testid="ev-control" />
                    <Input placeholder="Type (policy_document / test_report / audit / …)" value={f.evidence_type} onChange={(e) => setF({ ...f, evidence_type: e.target.value })} data-testid="ev-type" />
                    <Input placeholder="URL or storage path" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} data-testid="ev-url" />
                    <Input placeholder="Owner" value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} data-testid="ev-owner" />
                    <Textarea placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="md:col-span-2" data-testid="ev-desc" />
                </div>
                <Button className="mt-3" onClick={add} data-testid="ev-add"><Plus size={13} className="mr-1" /> Add evidence</Button>
            </div>
            <div className="rounded-xl border border-stone-200 dark:border-stone-700 divide-y divide-stone-100 dark:divide-stone-800">
                {rows.length === 0 && <div className="p-6 text-sm text-stone-500 text-center">No evidence recorded yet.</div>}
                {rows.map((r) => (
                    <div key={r.id} className="flex items-start justify-between gap-3 p-3">
                        <div>
                            <div className="text-sm font-medium">{r.control} <span className="ml-2 text-[10px] uppercase tracking-wider text-stone-500">{r.evidence_type}</span></div>
                            {r.description && <div className="text-xs text-stone-600 dark:text-stone-300 mt-1">{r.description}</div>}
                            <div className="text-[11px] text-stone-500 mt-1">Owner: {r.owner || "—"} · Recorded: {r.date_recorded?.slice(0, 10)}</div>
                        </div>
                        <button onClick={() => del(r.id)} className="text-stone-400 hover:text-rose-600" data-testid={`ev-del-${r.id}`}><Trash2 size={13} /></button>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Incidents() {
    const [rows, setRows] = useState([]);
    const [f, setF] = useState({ incident_type: "", risk_assessment: "", containment: "", status: "Open" });
    const load = () => api.get("/admin/compliance/incidents").then((r) => setRows(r.data.incidents));
    useEffect(() => { load(); }, []);
    const add = async () => {
        if (!f.incident_type) return toast.error("Incident type required");
        await api.post("/admin/compliance/incidents", f);
        toast.success("Incident recorded");
        setF({ incident_type: "", risk_assessment: "", containment: "", status: "Open" });
        load();
    };
    const patchStatus = async (id, status) => {
        const row = rows.find((r) => r.id === id);
        if (!row) return;
        await api.patch(`/admin/compliance/incidents/${id}`, { ...row, status });
        toast.success(`Incident marked ${status}`);
        load();
    };
    return (
        <div className="mt-4 space-y-4">
            <div className="rounded-xl border border-stone-200 dark:border-stone-700 p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3">Record new incident</div>
                <div className="grid md:grid-cols-2 gap-3">
                    <Input placeholder="Type (e.g. unauthorised login attempt)" value={f.incident_type} onChange={(e) => setF({ ...f, incident_type: e.target.value })} data-testid="inc-type" />
                    <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="inc-status" className="rounded-md border border-stone-300 bg-white px-2 py-2 text-sm">
                        {["Open", "Investigating", "Contained", "Closed"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <Textarea placeholder="Risk assessment" value={f.risk_assessment} onChange={(e) => setF({ ...f, risk_assessment: e.target.value })} className="md:col-span-2" data-testid="inc-risk" />
                    <Textarea placeholder="Containment steps taken" value={f.containment} onChange={(e) => setF({ ...f, containment: e.target.value })} className="md:col-span-2" data-testid="inc-contain" />
                </div>
                <Button className="mt-3" onClick={add} data-testid="inc-add"><Plus size={13} className="mr-1" /> Record incident</Button>
            </div>
            <div className="rounded-xl border border-stone-200 dark:border-stone-700 divide-y divide-stone-100 dark:divide-stone-800">
                {rows.length === 0 && <div className="p-6 text-sm text-stone-500 text-center">No incidents recorded.</div>}
                {rows.map((r) => (
                    <div key={r.id} className="flex items-start justify-between gap-3 p-3">
                        <div>
                            <div className="text-sm font-medium">{r.incident_type}</div>
                            {r.risk_assessment && <div className="text-xs text-stone-600 mt-1">{r.risk_assessment}</div>}
                            <div className="text-[11px] text-stone-500 mt-1">Detected: {r.detected_at?.slice(0, 16).replace("T", " ")}</div>
                        </div>
                        <select value={r.status} onChange={(e) => patchStatus(r.id, e.target.value)} className="rounded-md border border-stone-300 bg-white px-2 py-1 text-xs" data-testid={`inc-${r.id}-status`}>
                            {["Open", "Investigating", "Contained", "Closed"].map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                ))}
            </div>
        </div>
    );
}
